import React, { useRef, useEffect, useMemo, useState } from 'react';
// useCallback removed - only needed for city search
import { useNavigate, useSearchParams } from 'react-router-dom';
import { HiLocationMarker, HiGlobeAlt } from 'react-icons/hi';
// import { HiSearch, HiX } from 'react-icons/hi'; // Commented out - needed for city search
import CommunityEventCard from '../../components/events/CommunityEventCard/CommunityEventCard';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton/LoadingSkeleton';
import EmptyState from '../../components/ui/EmptyState/EmptyState';
import ChannelInitialIcon from '../../components/ui/ChannelInitialIcon/ChannelInitialIcon';
import EventsMap from '../../components/events/EventsMap/EventsMap';
import { useEvents } from '../../hooks/useEvents';
import { useChannels } from '../../hooks/useChannels';
import { useGeneral } from '../../contexts';
// import Services from '../../services/Services'; // Commented out - needed for city search
import { EventListItem, ChannelListItem } from '../../types';
import { generateEventUrl, generateCommunityUrl } from '../../utils/urlSlug';
import SEO from '../../components/SEO';
import transitionStyles from '../../styles/transitions.module.css';
import styles from './EventsListPage.module.css';

const EventsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { auth, language } = useGeneral();
  const { token, user } = auth;
  const { getTranslation } = language;
  const isMountedRef = useRef(true);
  const [showAllCommunities, setShowAllCommunities] = useState(false);
  const [filterOnline, setFilterOnline] = useState(false);
  const [filterAllEvents, setFilterAllEvents] = useState(true); // Default to All Events active
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);

  // City filter state (commented out - will be needed later)
  // const [citySearchQuery, setCitySearchQuery] = useState('');
  // const [selectedCity, setSelectedCity] = useState<string | null>(null);
  // const [citySearchResults, setCitySearchResults] = useState<EventListItem[]>([]);
  // const [searchingCity, setSearchingCity] = useState(false);
  // const cityInputRef = useRef<HTMLInputElement>(null);
  
  // Get search query from URL params
  const searchQuery = searchParams.get('search') || '';

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Fetch all events using closest dates endpoint
  const { events: allEvents, loading } = useEvents({
    token,
    loadingKey: 'events.list',
    useClosestDates: true
  });

  // Fetch user's communities (not popular)
  const { channels: userChannels } = useChannels({
    token,
    userID: user?.id,
    popular: false,
    loadingKey: 'events.userChannels'
  });

  // Separate admin communities from other communities
  const { adminCommunities, otherCommunities } = useMemo(() => {
    const admin: ChannelListItem[] = [];
    const other: ChannelListItem[] = [];
    
    userChannels.forEach((channel) => {
      if (channel.isAdmin === "1") {
        admin.push(channel as ChannelListItem);
      } else {
        other.push(channel as ChannelListItem);
      }
    });
    
    return { adminCommunities: admin, otherCommunities: other };
  }, [userChannels]);

  // Get joined channel IDs for filtering private channels
  const joinedChannelIds = useMemo(() => {
    return new Set(userChannels.map(ch => String(ch.channelID)));
  }, [userChannels]);

  // Filter events based on search query, date range, and private channel access.
  // "All Events" applies no online/in-person restriction.
  const filteredEvents = useMemo(() => {
    let filtered = allEvents;
    const now = Math.floor(Date.now() / 1000); // Current time in seconds
    
    // Filter by date - show event if it's currently active or upcoming
    filtered = filtered.filter((event) => {
      const startTime = event.eventStartDateTime;
      const endTime = event.eventEndDateTime;
      
      // Show event if:
      // 1. Event is currently active (now is between start and end)
      // 2. Event is upcoming (start time is in the future)
      // Don't show if event has ended (end time is in the past)
      const isActive = now >= startTime && now <= endTime;
      const isUpcoming = now < startTime;
      return isActive || isUpcoming;
    });
    
    // Filter out unlisted events (isPublic = "3")
    filtered = filtered.filter((event) => event.isPublic !== "3");

    // Filter out events from private channels where user is not a member
    filtered = filtered.filter((event) => {
      // If event has channel details, check if channel is private
      if (event.channelDetails) {
        const isPublic = event.channelDetails.isPublic === "1";
        const channelID = event.channelDetails.channelID || event.channelID;

        // Show event if channel is public OR user is a member
        if (isPublic) {
          return true;
        }

        // If channel is private, check if user is a member
        if (channelID && joinedChannelIds.has(String(channelID))) {
          return true;
        }

        // Private channel and user is not a member - hide event
        return false;
      }

      // If no channel details, show the event (conservative approach)
      return true;
    });
    
    // Apply online filter
    if (filterOnline) {
      filtered = filtered.filter((event) => event.isOnline === "1");
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((event) => {
        const title = (event.eventTitle || event.title || '').toLowerCase();
        const description = (event.description || '').toLowerCase();
        const location = (event.locationAddress || '').toLowerCase();
        const channel = (event.channelTitle || '').toLowerCase();
        
        return title.includes(query) || 
               description.includes(query) || 
               location.includes(query) || 
               channel.includes(query);
      });
    }
    
    // Filter by selected date/range
    if (selectedDate) {
      const selectedStart = new Date(selectedDate);
      selectedStart.setHours(0, 0, 0, 0);
      const selectedStartTimestamp = Math.floor(selectedStart.getTime() / 1000);
      
      if (selectedEndDate) {
        // Range selection
        const selectedEnd = new Date(selectedEndDate);
        selectedEnd.setHours(23, 59, 59, 999);
        const selectedEndTimestamp = Math.floor(selectedEnd.getTime() / 1000);
        
        filtered = filtered.filter((event) => {
          const eventStart = event.eventStartDateTime;
          const eventEnd = event.eventEndDateTime;
          // Event overlaps with selected range
          return (eventStart <= selectedEndTimestamp && eventEnd >= selectedStartTimestamp);
        });
      } else {
        // Single day selection
        const selectedEndTimestamp = selectedStartTimestamp + 86400 - 1; // End of day
        
        filtered = filtered.filter((event) => {
          const eventStart = event.eventStartDateTime;
          const eventEnd = event.eventEndDateTime;
          // Event overlaps with selected day
          return (eventStart <= selectedEndTimestamp && eventEnd >= selectedStartTimestamp);
        });
      }
    }
    
    return filtered;
  }, [allEvents, filterOnline, searchQuery, joinedChannelIds, selectedDate, selectedEndDate]);

  // Get communities to display
  // Show first 3 admin communities, then remaining admin communities, then other communities when expanded
  const { displayedAdminCommunities, remainingAdminCommunities } = useMemo(() => {
    const first3Admin = adminCommunities.slice(0, 3);
    const remainingAdmin = adminCommunities.slice(3);
    return { displayedAdminCommunities: first3Admin, remainingAdminCommunities: remainingAdmin };
  }, [adminCommunities]);

  const handleEventClick = (event: EventListItem) => {
    navigate(generateEventUrl(event.eventTitle || event.title, event.eventID));
  };

  const handleCommunityClick = (community: ChannelListItem) => {
    const communityTitle = community.channelTitle || community.title;
    navigate(generateCommunityUrl(communityTitle, community.channelID));
  };

  // City search function (commented out - will be needed later)
  // const handleCitySearch = useCallback(async (query: string) => {
  //   if (!token || !query.trim()) {
  //     setCitySearchResults([]);
  //     return;
  //   }
  //   setSearchingCity(true);
  //   try {
  //     const response = await Services.SocialHiveSearchEventByCity(token, query.trim());
  //     if (response.data.status && response.data.result) {
  //       setCitySearchResults(response.data.result as EventListItem[]);
  //     } else {
  //       setCitySearchResults([]);
  //     }
  //   } catch (error) {
  //     console.error('City search error:', error);
  //     setCitySearchResults([]);
  //   } finally {
  //     setSearchingCity(false);
  //   }
  // }, [token]);

  // // Debounced city search
  // useEffect(() => {
  //   const debounceTimer = setTimeout(() => {
  //     if (citySearchQuery.trim()) {
  //       handleCitySearch(citySearchQuery);
  //     } else {
  //       setCitySearchResults([]);
  //     }
  //   }, 300);
  //   return () => clearTimeout(debounceTimer);
  // }, [citySearchQuery, handleCitySearch]);

  const handleOnlineFilterClick = () => {
    if (!filterOnline) {
      setFilterOnline(true);
      setFilterAllEvents(false);
    }
  };

  const handleAllEventsFilterClick = () => {
    if (!filterAllEvents) {
      setFilterAllEvents(true);
      setFilterOnline(false);
    }
  };

  // const handleClearCity = () => {
  //   setSelectedCity(null);
  //   setCitySearchQuery('');
  //   setCitySearchResults([]);
  // };

  const handleViewAllClick = () => {
    setShowAllCommunities(!showAllCommunities);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const newSearchParams = new URLSearchParams(searchParams);
    if (value.trim()) {
      newSearchParams.set('search', value);
    } else {
      newSearchParams.delete('search');
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleDateSelect = (date: Date | null, endDate: Date | null) => {
    setSelectedDate(date);
    setSelectedEndDate(endDate);
  };

  return (
    <>
      <SEO
        title={getTranslation('navigation.events') || 'Events'}
        description="Browse upcoming events on ToGather."
        url={typeof window !== 'undefined' ? `${window.location.origin}/events` : undefined}
        lang={language.language}
      />
      <div className={styles.eventsListPage}>
        <div className={styles.mainContent}>
        <div className={styles.eventsList}>
          {loading ? (
            <div className={styles.loadingContainer}>
              {[1, 2, 3, 4, 5].map((i) => (
                <LoadingSkeleton key={i} width="100%" height={150} />
              ))}
            </div>
          ) : filteredEvents.length > 0 ? (
            <div className={styles.eventsListContainer}>
              {filteredEvents.map((event, index) => (
                <div key={event.eventID} className={transitionStyles.staggerItem} style={{ animationDelay: `${Math.min(index * 50, 300)}ms` }}>
                  <CommunityEventCard
                    event={event}
                    onClick={handleEventClick}
                    isLast={index === filteredEvents.length - 1}
                  />
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title={getTranslation('events.eventNotFound')} />
          )}
        </div>
      </div>

      <div className={styles.sidebar}>
        <div className={styles.filters}>
          <button
            className={`${styles.locationButton} ${filterAllEvents ? styles.locationButtonActive : ''}`}
            onClick={handleAllEventsFilterClick}
          >
            {/* @ts-ignore */}
            <HiLocationMarker size={16} />
            <span>{getTranslation('events.allEvents') || 'Tüm Etkinlikler'}</span>
          </button>

          <button
            className={`${styles.onlineButton} ${filterOnline ? styles.onlineButtonActive : ''}`}
            onClick={handleOnlineFilterClick}
          >
            {/* @ts-ignore */}
            <HiGlobeAlt size={16} />
            <span>{getTranslation('events.online')}</span>
          </button>
        </div>

        <div className={styles.communitiesSection}>
          <div className={styles.communitiesHeader}>
            <h3>{getTranslation('events.yourCommunities')}</h3>
            {(remainingAdminCommunities.length > 0 || otherCommunities.length > 0) && (
              <button 
                className={styles.viewAll}
                onClick={handleViewAllClick}
              >
                {showAllCommunities ? getTranslation('common.viewLess') : getTranslation('common.viewAll')}
              </button>
            )}
          </div>
          <div className={styles.communitiesList}>
            {(displayedAdminCommunities.length > 0 || (showAllCommunities && (remainingAdminCommunities.length > 0 || otherCommunities.length > 0))) ? (
              <>
                {/* First 3 admin communities - always shown */}
                {displayedAdminCommunities.map((channel) => (
                  <div
                    key={channel.channelID}
                    className={styles.communityItem}
                    onClick={() => handleCommunityClick(channel)}
                  >
                    <div className={styles.communityIcon}>
                      {channel.logo || channel.channelLogo ? (
                        <img
                          src={channel.logo || channel.channelLogo || ''}
                          alt={channel.title}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <ChannelInitialIcon 
                          channelName={channel.channelTitle || channel.title}
                          className={styles.communityIcon}
                        />
                      )}
                    </div>
                    <div className={styles.communityInfo}>
                      <div className={styles.communityName}>{channel.channelTitle || channel.title}</div>
                      <button 
                        className={styles.viewCommunity}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCommunityClick(channel);
                        }}
                      >
                        {getTranslation('events.viewCommunity')}
                      </button>
                    </div>
                  </div>
                ))}
                {/* Remaining admin communities - shown when expanded */}
                {showAllCommunities && remainingAdminCommunities.length > 0 && (
                  <>
                    {remainingAdminCommunities.map((channel) => (
                      <div
                        key={channel.channelID}
                        className={styles.communityItem}
                        onClick={() => handleCommunityClick(channel)}
                      >
                        <div className={styles.communityIcon}>
                          {channel.logo || channel.channelLogo ? (
                            <img
                              src={channel.logo || channel.channelLogo || ''}
                              alt={channel.title}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <ChannelInitialIcon 
                              channelName={channel.channelTitle || channel.title}
                              className={styles.communityIcon}
                            />
                          )}
                        </div>
                        <div className={styles.communityInfo}>
                          <div className={styles.communityName}>{channel.channelTitle || channel.title}</div>
                          <button 
                            className={styles.viewCommunity}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCommunityClick(channel);
                            }}
                          >
                            {getTranslation('events.viewCommunity')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
                {/* Other (joined) communities - shown when expanded, after remaining admin communities */}
                {showAllCommunities && otherCommunities.length > 0 && (
                  <>
                    {otherCommunities.map((channel) => (
                      <div
                        key={channel.channelID}
                        className={styles.communityItem}
                        onClick={() => handleCommunityClick(channel)}
                      >
                        <div className={styles.communityIcon}>
                          {channel.logo || channel.channelLogo ? (
                            <img
                              src={channel.logo || channel.channelLogo || ''}
                              alt={channel.title}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <ChannelInitialIcon 
                              channelName={channel.channelTitle || channel.title}
                              className={styles.communityIcon}
                            />
                          )}
                        </div>
                        <div className={styles.communityInfo}>
                          <div className={styles.communityName}>{channel.channelTitle || channel.title}</div>
                          <button 
                            className={styles.viewCommunity}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCommunityClick(channel);
                            }}
                          >
                            {getTranslation('events.viewCommunity')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </>
            ) : (
              <EmptyState title={getTranslation('communities.communityNotFound')} />
            )}
          </div>
        </div>

        {/* Calendar Section (temporarily hidden) */}

        {/* Map Section */}
        {!filterOnline && (
          <div className={styles.mapSection}>
            <EventsMap
              events={filteredEvents}
              isOnlineFilterActive={filterOnline}
            />
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default EventsListPage;
